import { computed } from "vue";
import { store } from "@/store";

import { fabric } from "fabric";
import { drawLine, drawRect, drawCircle } from "@/canvas-api/CreateObjects";

import {
  labelCanvasCircleProps,
  labelCanvasRectProps,
  labelCanvasLineProps,
} from "@/canvas-api/ObjectProperties";

import { SheetLabel, Dimensions } from "@/types";

const labelWidth = computed<number>(() => store.getters.labelWidth);
const labelHeight = computed<number>(() => store.getters.labelHeight);
const bleedWidth = computed<number>(() => store.getters.bleedWidth);
const bleedHeight = computed<number>(() => store.getters.bleedHeight);
const safeZoneWidth = computed<number>(() => store.getters.safeZoneWidth);
const safeZoneHeight = computed<number>(() => store.getters.safeZoneHeight);
const dimensions = computed<Dimensions>(() => store.getters.dimensions);
const currentLabel = computed<SheetLabel>(() => store.getters.currentLabel);

const dpi = computed<number>(() => store.getters.dpi);

const isRotated = computed<boolean>(() =>
  store.getters.canvasOrientation === "rotated" ? true : false
);
const cornerRadius = computed<number>(() =>
  currentLabel.value.corners === "Rounded"
    ? currentLabel.value.corner_radius > 0
      ? currentLabel.value.corner_radius * dpi.value
      : 0.125 * dpi.value
    : 0
);

export const makeRectLabel = (canvas: fabric.Canvas, bgColor = "#fff"): void => {
  const { left, top } = canvas.getCenter();

  //Make labelCanvas size of bleed
  const labelCanvasProps: fabric.IRectOptions = labelCanvasRectProps(
    "labelCanvas",
    left,
    top,
    bleedWidth.value,
    bleedHeight.value,
    "",
    bgColor,
    cornerRadius.value
  );

  //Actual label size
  const labelSizeProps: fabric.IRectOptions = labelCanvasRectProps(
    "actualLabelSize",
    left,
    top,
    labelWidth.value,
    labelHeight.value,
    "#F062B1",
    "transparent",
    cornerRadius.value
  );

  const bleedRectProps: fabric.IRectOptions = labelCanvasRectProps(
    "bleedLine",
    left,
    top,
    bleedWidth.value,
    bleedHeight.value,
    "#66CFF3",
    "transparent",
    cornerRadius.value
  );

  const labelCanvas = drawRect(labelCanvasProps);
  const labelSize = drawRect(labelSizeProps); // Creating a transparent rect the actual size of the label
  const bleedRect = drawRect(bleedRectProps);

  const safeZoneLines = drawCanvasRectSafe(
    canvas,
    isRotated.value ? safeZoneHeight.value : safeZoneWidth.value,
    isRotated.value ? safeZoneWidth.value : safeZoneHeight.value
  );

  if (isRotated.value) {
    labelCanvas.set({ angle: 90 });
    labelSize.set({ angle: 90 });
    bleedRect.set({ angle: 90 });
  }

  canvas.add(labelCanvas, labelSize, bleedRect, ...safeZoneLines).renderAll();
};

export const drawCanvasRectSafe = (
  canvas: fabric.Canvas,
  safeLineWidth: number,
  safeLineHeight: number
): fabric.Line[] => {
  const { left, top } = canvas.getCenter();

  const safeZoneProps: fabric.ILineOptions = labelCanvasLineProps("safeZone", "#555", [5, 10]);

  const safeZoneLines = [
    drawLine(
      [
        left - safeLineWidth / 2,
        top - safeLineHeight / 2,
        left + safeLineWidth / 2,
        top - safeLineHeight / 2,
      ],
      safeZoneProps
    ),
    drawLine(
      [
        left - safeLineWidth / 2,
        top - safeLineHeight / 2,
        left - safeLineWidth / 2,
        top + safeLineHeight / 2,
      ],
      safeZoneProps
    ),
    drawLine(
      [
        left + safeLineWidth / 2,
        top - safeLineHeight / 2,
        left + safeLineWidth / 2,
        top + safeLineHeight / 2,
      ],
      safeZoneProps
    ),
    drawLine(
      [
        left - safeLineWidth / 2,
        top + safeLineHeight / 2,
        left + safeLineWidth / 2,
        top + safeLineHeight / 2,
      ],
      safeZoneProps
    ),
  ];

  return safeZoneLines;
};

export const drawCanvasRectBleed = (canvas: fabric.Canvas, bleedLineWidth: number, bleedLineHeight: number): fabric.Line[] => {
  const { left, top } = canvas.getCenter();

  const bleedLineProps: fabric.ILineOptions = labelCanvasLineProps("bleedLine", "#66CFF3");
  const bleedLinePropsTop: fabric.ILineOptions = labelCanvasLineProps("bleedLineTop", "#66CFF3");

  const bleedLines = [
    drawLine(
      [
        left - bleedLineWidth / 2,
        top - bleedLineHeight / 2,
        left + bleedLineWidth / 2,
        top - bleedLineHeight / 2,
      ],
      bleedLinePropsTop
    ),
    drawLine(
      [
        left - bleedLineWidth / 2,
        top - bleedLineHeight / 2,
        left - bleedLineWidth / 2,
        top + bleedLineHeight / 2,
      ],
      bleedLineProps
    ),
    drawLine(
      [
        left + bleedLineWidth / 2,
        top - bleedLineHeight / 2,
        left + bleedLineWidth / 2,
        top + bleedLineHeight / 2,
      ],
      bleedLineProps
    ),
    drawLine(
      [
        left - bleedLineWidth / 2,
        top + bleedLineHeight / 2,
        left + bleedLineWidth / 2,
        top + bleedLineHeight / 2,
      ],
      bleedLineProps
    ),
  ];

  return bleedLines;
};

export const makeCircleLabel = (canvas: fabric.Canvas, bgColor = "#fff"): void => {
  const { top, left } = canvas.getCenter();

  const labelCanvasProps: fabric.ICircleOptions = labelCanvasCircleProps(
    "labelCanvas",
    left,
    top,
    bleedWidth.value / 2,
    "",
    bgColor
  );

  const labelSizeProps: fabric.ICircleOptions = labelCanvasCircleProps(
    "actualLabelSize",
    left,
    top,
    labelWidth.value / 2,
    "#F062B1",
    "transparent"
  );

  const labelCanvas = drawCircle(labelCanvasProps);
  const labelSize = drawCircle(labelSizeProps); // Creating a transparent rect the actual size of the label

  const safeZoneProps: fabric.ICircleOptions = labelCanvasCircleProps(
    "safeZone",
    left,
    top,
    safeZoneWidth.value / 2,
    "black",
    "rgba(0,0,0,0)",
    [15, 10]
  );
  const safeZone = drawCircle(safeZoneProps);

  const bleedLineProps: fabric.ICircleOptions = labelCanvasCircleProps(
    "bleedLine",
    left,
    top,
    bleedWidth.value / 2,
    "#66CFF3",
    "rgba(0,0,0,0)"
  );
  const bleedLine = drawCircle(bleedLineProps);

  if (isRotated.value) {
    labelCanvas.set({ angle: 90 });
    labelSize.set({ angle: 90 });
    safeZone.set({ angle: 90 });
    bleedLine.set({ angle: 90 });
  }

  canvas.add(labelCanvas, labelSize, safeZone, bleedLine).renderAll();
};

const getMask = (maskShape?: string): Promise<fabric.Object> => {
  // prettier-ignore
  return new Promise((resolve) => {
		if (!maskShape) {      
      if (currentLabel.value.mask_url) {
        fabric.loadSVGFromURL(currentLabel.value.mask_url, function( results, options ) {
          const labelCanvas = fabric.util.groupSVGElements(results, options);
  
          resolve(labelCanvas);
        });
      } else {        
        fabric.loadSVGFromURL(require(`@/assets/labels/${currentLabel.value.mask_id}.svg`), function( results, options ) {
          const labelCanvas = fabric.util.groupSVGElements(results, options);
  
          resolve(labelCanvas);
        });
      }
		} else {
			fabric.loadSVGFromURL(require(`@/assets/masks/mask-${maskShape}.svg`), function( results, options ) {
				const labelCanvas = fabric.util.groupSVGElements(results, options);

				resolve(labelCanvas);
			});
		}
	});
};

// prettier-ignore
const getScalesForLabelFromMask = (labelCanvas: fabric.Object) => {
  const labelWidth = dimensions.value.width * dimensions.value.units.dpi;
  const labelHeight = dimensions.value.height * dimensions.value.units.dpi;

  const scaleX = labelWidth / labelCanvas.width!;
  const scaleY = labelHeight / labelCanvas.height!;

  const bleedWidth = (dimensions.value.width + (dimensions.value.units.offSet * 2)) * dimensions.value.units.dpi;
  const bleedHeight = (dimensions.value.height + (dimensions.value.units.offSet * 2)) * dimensions.value.units.dpi;

  const bleedScaleX = bleedWidth / labelCanvas.width!;
  const bleedScaleY = bleedHeight / labelCanvas.height!;
  
  const safeZoneWidth = (dimensions.value.width - (dimensions.value.units.offSet * 2)) * dimensions.value.units.dpi;
  const safeZoneHeight = (dimensions.value.height - (dimensions.value.units.offSet * 2)) * dimensions.value.units.dpi;

  const safeZoneScaleX = safeZoneWidth / labelCanvas.width!;
  const safeZoneScaleY = safeZoneHeight / labelCanvas.height!;

  return {
    scaleX, scaleY, bleedScaleX, bleedScaleY, safeZoneScaleX, safeZoneScaleY
  }
}

const getLabelPartsFromMask = (
  labelCanvas: fabric.Object,
  scaleX: number,
  scaleY: number,
  name: string,
  fill: string,
  stroke: string,
  strokeDashArray?: number[]
): Promise<fabric.Object> => {
  return new Promise((resolve, reject) => {
    labelCanvas.clone(function(clone: fabric.Object) {
      clone.set({
        scaleX,
        scaleY,
        name,
        fill,
        stroke,
        selectable: false,
        evented: false,
      });

      if (strokeDashArray) {
        clone.set({
          strokeDashArray,
        });
      }

      resolve(clone);
    });
  });
};

export const makeLabelFromMask = async (canvas: fabric.Canvas, bgColor = "#fff", maskShape?: string): Promise<void> => {
  const labelShape = await getMask(maskShape);
  const labelSize = fabric.util.object.clone(labelShape);

  const { left, top } = canvas.getCenter();

  const {
    scaleX,
    scaleY,
    bleedScaleX,
    bleedScaleY,
    safeZoneScaleX,
    safeZoneScaleY,
  } = getScalesForLabelFromMask(labelShape);

  labelShape.set({
    left,
    top,
    scaleX: bleedScaleX,
    scaleY: bleedScaleY,
    selectable: false,
    evented: false,
    originX: "center",
    originY: "center",
    fill: bgColor,
    stroke: "",
    name: "labelCanvas",
  });

  labelSize.set({
    left,
    top,
    scaleX,
    scaleY,
    selectable: false,
    evented: false,
    originX: "center",
    originY: "center",
    fill: "transparent",
    stroke: "#F062B1",
    name: "actualLabelSize",
  });

  const bleed = await getLabelPartsFromMask(
    labelShape,
    bleedScaleX,
    bleedScaleY,
    "bleedLine",
    "transparent",
    "#66CFF3"
  );

  const safeZone = await getLabelPartsFromMask(
    labelShape,
    safeZoneScaleX,
    safeZoneScaleY,
    "safeZone",
    "transparent",
    "#000",
    [15, 10]
  );

  if (isRotated.value) {
    labelShape.set({ angle: 90 });
    labelSize.set({ angle: 90 });
    safeZone.set({ angle: 90 });
    bleed.set({ angle: 90 });
  }

  canvas.add(labelShape, labelSize, safeZone, bleed).renderAll();
};

export const makeFlyerLabel = (canvas: fabric.Canvas, bgColor = "#fff", maskShape?: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    fabric.loadSVGFromURL(require(`@/assets/masks/mask-${maskShape}.svg`), async function(
      results,
      options
    ) {
      const labelCanvas = fabric.util.groupSVGElements(results, options) as fabric.Object;
      const labelSize = fabric.util.object.clone(labelCanvas);

      const { left, top } = canvas.getCenter();

      if (labelCanvas.width && labelCanvas.height) {
        const scaleX = labelWidth.value / labelCanvas.width;
        const scaleY = labelHeight.value / labelCanvas.height;

        labelCanvas.set({
          left,
          top,
          scaleX,
          scaleY,
          selectable: false,
          evented: false,
          originX: "center",
          originY: "center",
          fill: bgColor,
          stroke: "#F062B1",
          name: "labelCanvas",
        });

        labelSize.set({
          left,
          top,
          scaleX,
          scaleY,
          selectable: false,
          evented: false,
          originX: "center",
          originY: "center",
          fill: "transparent",
          stroke: "#F062B1",
          name: "actualLabelSize",
          type: "path",
        });

        const safeZoneProps: fabric.ICircleOptions = labelCanvasCircleProps(
          "safeZone",
          left,
          top,
          safeZoneWidth.value / 2,
          "black",
          "rgba(0,0,0,0)",
          [15, 10]
        );
        const safeZone = drawCircle(safeZoneProps);

        const bleedLineProps: fabric.ICircleOptions = labelCanvasCircleProps(
          "bleedLine",
          left,
          top,
          bleedWidth.value / 2,
          "#66CFF3",
          "rgba(0,0,0,0)"
        );
        const bleed = drawCircle(bleedLineProps);

        //Make a clone of the labelCanvas layer, make it transparent, and slap it on top of the rest of the layers
        //This gives the label the center piece that should cover objects underneath it
        labelCanvas.clone(function(clone: any) {
          //Find the first instance of Z and remove those path coordinates to isolate the circle we want to layer

          //COULD CAUSE ISSUES IN THE FURUTE, DEPENDS ON HOW SVGS ARE MADE
          let zIndex = clone.path.length;
          for (let i = 0; i < clone.path.length; i++) {
            if (clone.path[i][0] === "z") {
              zIndex = i;
              break;
            }
          }

          clone.path = clone.path.slice(zIndex + 1, clone.path.length);

          clone.set({
            name: "flyerTopLayer",
            left,
            top,
            evented: false,
            selectable: false,
            fill: "#edf0f2",
          });

          if (isRotated.value) {
            labelCanvas.set({ angle: 90 });
            labelSize.set({ angle: 90 });
            safeZone.set({ angle: 90 });
            bleed.set({ angle: 90 });
          }

          canvas.add(labelCanvas, labelSize, safeZone, bleed, clone).renderAll();
          resolve();
        });
      }
    });
  });
};
